import { Injectable } from '@angular/core';
import { DxAspNetDataXsrfAwareService } from '@tools';
import { UrlEnhancerService } from '@url';
import { Config } from '@config';

import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { AuthHttp } from '@http';
import { TenantService } from '@tenant';
import { IdConverterService, IdTypes, TokenConverterService } from '@converter';
import { custom } from 'devextreme/ui/dialog';
import { __i18n } from '@localisation';
import { lastValueFrom } from 'rxjs';
import { SortDescriptor } from 'devextreme/data';


export interface ThirdPartyApp {
    name : string;
    isEnabled : boolean;
    clientUri : string;
    logoUri : string;
    redirectUris : string[];
    postLogoutRedirectUris : string[];
    secrets?: any[];
    tenants?: any[];
}


@Injectable()
export class ThirdPartyAppsService {

    constructor(
        private urlEnhancerService: UrlEnhancerService,
        private config: Config,
        private dxAspNetXsrfAwareService: DxAspNetDataXsrfAwareService,
        private http: AuthHttp,
        private tenantService: TenantService,
        private converterService: IdConverterService
    ){}

    private mapSortItem(sort: any)
    {
        const result: { [id: string] : string } = {};
        if (typeof sort === 'string') {
            result[sort] = 'asc';
        } else {
            result[sort.selector] = sort.desc ? "desc" : "asc";
        }

        return result;
    }

    public getDataSource(): DataSource{
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.listThirdPartyApps, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant)
        });

        const store = new CustomStore({
            load : async(options) => {
                let sort: Array<{ [id: string] : string }> = [{name: "asc"}];
                if (options.sort !== null) {
                    if (Array.isArray(options.sort)) {
                        sort = options.sort.map(this.mapSortItem);
                    } else {
                        sort = [this.mapSortItem(options.sort)];
                    }
                }

                let requestUrl = url +
                    (url.indexOf('?') >= 0 ? '&' : '?') +
                    'skip=' + (options.skip ?? 0) +
                    '&take=' + (options.take ?? 10) +
                    '&sortBy=' + encodeURIComponent(JSON.stringify(sort));

                if (options.filter && typeof(options.filter.filterValue) === 'string') {
                    requestUrl += '&search=' + encodeURIComponent(options.filter.filterValue);
                }

                const result = await lastValueFrom(this.http.getJSON(requestUrl));
                return {
                    totalCount: result.totalCount,
                    data: result.items
                };
            }
        });

        return new DataSource({
            key: 'id',
            paginate: true,
            store
       });
    }

    public async getThirdPartyApp(appId: string){
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.thirdPartyApp, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant),
            thirdpartyid: appId
        });
        return lastValueFrom(this.http.getJSON(url));
    }

    public async createThirdPartyApp(data: ThirdPartyApp){
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.listThirdPartyApps, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant)
        });
        return lastValueFrom(this.http.postJSON(url, data));
    }

    public async updateThirdPartyApp(id: string, data: ThirdPartyApp){
        delete data.secrets;
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.listThirdPartyApps, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant)
        });
        return lastValueFrom(this.http.putJSON(url + '/' + id, data));
    }

    public async deleteThirdPartyApp(appId: string){
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.thirdPartyApp, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant),
            thirdpartyid: appId
        });
        return lastValueFrom(this.http.delete(url))
    }

    public async registerThirdPartyApp(appId: string){
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.thirdPartyApp, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant),
            thirdpartyid: appId
        });
        return lastValueFrom(this.http.post(url, {}))
    }

    public async updatePrimarySecret(appId) { 
        if(!await this.confirmNewSecret()){
            return;
        }
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.thirdPartyPrimarySecret, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant),
            thirdpartyid: appId
        });
        return lastValueFrom(this.http.put(url, { }));
    }

    public async updateSecondarySecret(appId) {
        if(!await this.confirmNewSecret()){
            return;
        }
        const url = this.urlEnhancerService.replaceParams(this.config.services.thirdPartyApps.thirdPartySecondarySecret, {
            tenantid :  this.converterService.convertToExternal(this.tenantService.getCurrentTenantId(), IdTypes.tenant),
            thirdpartyid: appId
        });
        return lastValueFrom(this.http.put(url, {}));
    }


    private confirmNewSecret() {
        return new Promise((resolve, reject) => {
            const dialog = custom({
                title: __i18n('Confirm', 'third-party-app.confirm-new-secret.title'),
                message: __i18n(
                    'When you renew this secret, the previous secret will no longer be valid. Make sure, it is not used anymore. This operation cannot be undone. Really renew?',
                    'third-party-app.confirm-new-secret.message'
                ),
                buttons: [
                    {
                        text: __i18n('Yes', 'third-party-app.confirm-new-secret.button.yes'),
                        onClick: () => {
                            dialog.hide();
                            resolve(true);
                        }
                    },
                    {
                        text:  __i18n('No', 'third-party-app.confirm-new-secret.button.no'),
                        onClick: () => {
                            dialog.hide();
                            resolve(false);
                        }
                    }
                ]
            });
            dialog.show();
        });
    }
}